<template>
  <div class="ai-chat-overlay" @click.self="closeModal">
    <div class="ai-chat-modal dark" role="dialog" aria-modal="true">
      <div class="ai-chat-header">
        <div class="ai-chat-title">AIに相談して探す</div>
        <div class="ai-chat-header-actions">
          <button type="button" class="ai-chat-send" @click="confirmResetChat()">最初からやり直す</button>
          <button type="button" class="ai-chat-close" aria-label="チャットを閉じる" @click="closeModal()">×</button>
        </div>
      </div>
      <div class="ai-chat-body" ref="chatBody">
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="ai-chat-row"
          :class="[
            message.side === 'right' ? 'right' : 'left',
            message.type === 'link' ? 'link' : ''
          ]"
        >
          <div
            class="ai-chat-avatar"
            :class="message.side === 'right' ? 'right' : 'left'"
          >
            <i v-if="message.side === 'right'" class="el-icon-user"></i>
            <i v-else class="el-icon-s-comment"></i>
          </div>
          <div v-if="message.type === 'link'" class="ai-chat-bubble ai-chat-link-bubble">
            <button
              type="button"
              class="ai-chat-link-button"
              @click="searchBySuggest(message)"
            >
              {{ message.text || 'リンクを開く' }}
            </button>
          </div>
          <div v-else class="ai-chat-bubble">
            <div
              v-if="message.isHtml"
              class="ai-chat-text ai-chat-text-html"
              v-html="message.text"
            ></div>
            <div v-else class="ai-chat-text">{{ message.text }}</div>
          </div>
        </div>
        <div v-if="isLoading" class="ai-chat-row left">
          <div class="ai-chat-avatar left">
            <i class="el-icon-s-comment"></i>
          </div>
          <div class="ai-chat-bubble ai-chat-loading-bubble">
            <span class="ai-chat-loading-dot"></span>
            <span class="ai-chat-loading-dot"></span>
            <span class="ai-chat-loading-dot"></span>
          </div>
        </div>
      </div>
      <div class="ai-chat-input-wrap">
        <div class="ai-chat-input">
          <textarea
            id="user-question"
            class="ai-chat-input-field"
            v-model="question"
            :disabled="isLoading"
            placeholder="メッセージを入力"
            rows="3"
          ></textarea>
          <button
            type="button"
            @click="handleSubmitButtonClick()"
            class="ai-chat-send"
            :class="{
              'is-loading': isLoading && !canCancelRequest && (pendingLogId === 0),
              'is-cancel': canCancelRequest
            }"
            :disabled="isSubmitDisabled"
          >{{ submitButtonText }}</button>
        </div>
        <div class="ai-chat-note">{{ aiOutputDisclaimerText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { AI_OUTPUT_DISCLAIMER_TEXT } from "@/utils/aiOutputDisclaimer";

const root = process.env.VUE_APP_API;
const SuggestSearchConfigAPI = root + "/api/search/suggest_search_condition_chat";

const CHAT_STATUS = {
  RELOAD: 1,
  RESET: 2,
  QUESTION: 4,
  CHECK: 5,
  CANCEL: 6,
  CHECK_ANSWER: 8,
};
const LOG_STATUS = {
  ACTIVE: 1,
  ERROR: 3,
  CANCEL:4,
  WAIT_RESPONSE: 5,
}

const DEFAULT_TRACE_ID = "0_0";
const DEFAULT_VERSION = "0.0.0";
const POLL_DELAY_MS = 5000;

export default {
  name: "aiChat",
  props: {
    snsType: {
      type: String
    }
  },
  data() {
    return {
      question: "",
      isLoading: false,
      cancelRequested: false,
      pendingLogId: 0,
      pollTimerId: null,
      appendedToBody: false,
    };
  },
  mounted(){
    this.appendToBody();
    this.load();
  },
  computed: {
    token() {
      return this.$store.state.token;
    },
    user_token() {
      return this.$store.state.user_token;
    },
    messages(){
      return this.$store.state.aiChat.logs[this.snsType].messages;
    },
    pendingState() {
      return this.$store.state.aiChat.logs[this.snsType].pending;
    },
    trace_id() {
      return this.$store.state.aiChat.logs[this.snsType].trace_id;
    },
    version(){
      return this.$store.state.aiChat.version;
    },
    canSendQuestion() {
      return this.isValidQuestion(this.question);
    },
    canCancelRequest() {
      return this.isLoading && !this.cancelRequested;
    },
    isSubmitDisabled() {
      return this.cancelRequested || (!this.canCancelRequest && !this.canSendQuestion);
    },
    aiOutputDisclaimerText() {
      return AI_OUTPUT_DISCLAIMER_TEXT;
    },
    submitButtonText() {
      if (this.canCancelRequest) {
        return "キャンセル";
      }
      if (this.cancelRequested) {
        return "キャンセル中";
      }
      return "送信";
    }
  },
  methods: {
    appendToBody() {
      if (!this.$el || this.appendedToBody || typeof document === "undefined") {
        return;
      }
      document.body.appendChild(this.$el);
      this.appendedToBody = true;
    },
    closeModal() {
      this.$emit("close");
    },
    isValidQuestion(question) {
      if (!question || question.trim() === "") {
        return false;
      }
      return /[A-Za-z0-9\u3040-\u30FF\u3400-\u9FFF\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A]/.test(question);
    },
    load(){
      // 最新データか確認
      this.getSuggestSearchCondfig(
        CHAT_STATUS.CHECK,
        "",
        0,
        res => {
          const activeLogId = res.data.logId || 0;
          const activeLogStatus = res.data.logStatus || 0;
          if(this.version !== res.data.version){
            // apiのversionが更新されているのでリセット
            this.$store.dispatch("resetAiChat");
            this.$store.dispatch("setAiChatTraceId", {
              snsType: this.snsType,
              trace_id: res.data.traceId,
            });
            this.reloadChat(activeLogId);
          }else if(activeLogId === 0){
            // アクティブなログ無し一応localstrageリセット
            this.reloadChat(activeLogId);
          }else{
            // traceIdを更新
            this.$store.dispatch("setAiChatTraceId", {
              snsType: this.snsType,
              trace_id: res.data.traceId,
            });
            // pending状態であればpending再開
            if (this.shouldResumePendingRequest(activeLogId, activeLogStatus)) {
              this.resumePendingRequest(activeLogId);
              this.renderMessage();
              return;
            }
            // messagesがないならデータ消えてる。再取
            if(this.messages.length === 0){
            // アクティブなログ無し一応localstrageリセット
              this.reloadChat(activeLogId);
            }else if(this.getLatestMessageLogId() === activeLogId) {
              // OK
              this.renderMessage();
            }else{
              // 一致しないならデータ欠けの可能性。サーバから再取得
              this.reloadChat(activeLogId);
            }
          }
        },
          () => {}
      );
    },
    reloadChat(activeLogId){
      this.getSuggestSearchCondfig(
        CHAT_STATUS.RELOAD,
        "",
        0,
        res => {
          // ブラウザ履歴を消して
          this.$store.dispatch("clearAiChatMessages", this.snsType);
          // start
          this.startChat()
          this.$store.dispatch("setAiChatTraceId", {
            snsType: this.snsType,
            trace_id: res.data.traceId,
          });
          if( res.data.history && res.data.history.length > 0) {
            res.data.history.forEach(log => {
              this.addUserMessage(log.user_question);
              this.addAiMessage(
                log.no, 
                log.hit_count, 
                log.ai_response, 
                log.searchCriteriaPreviews,
                log.userMessages);
              this.addLinkMessage(log.no, log.convertedSearchCriteria);
            })
          }
          this.resumePendingRequest(activeLogId);
        },
          () => {}
      );
    },
    startChat(){
      // 会話開始するだけ
      const defaultMessage = `
<p>インフルエンサー検索アシスタントです！ どのようなインフルエンサーをお探しですか？ 以下のようないろいろな切り口で、思いつくまま自由に入力してください。</br>
  必ず**【1人あたりの想定予算】**をご入力ください。</p>
<p>💡 以下は入力例です。1回の検索では1つの依頼だけ入力してください。</br>
「予算5万円で、文字入れが得意な20代向け美容系インフルエンサー」</br>
「予算10万円で、[URL]の人に似ていて、PR感が強すぎない人」</br>
「予算3万円で、顔出しありのコスメの置き画が綺麗な人を探して」</br>
      `.trim();
      this.addMessage({
        side: "left",
        text: defaultMessage,
        isHtml: true
      });
    },
    confirmResetChat(){
      if (this.isLoading) {
        return;
      }
      this.$confirm(
        "現在のチャット履歴が消去され、新しい会話が始まります。この操作は取り消せません。",
        "チャットを最初からやり直しますか？",
        {
          cancelButtonText: "キャンセル",
          confirmButtonText: "最初からやり直す",
          type: "warning",
        }
      )
        .then(() => {
          this.resetChat();
        })
        .catch(() => {});
    },
    resetChat(){
      // 会話リセット
      // サーバ側でセッション終了させて
      this.isLoading = true;
      this.getSuggestSearchCondfig(
        CHAT_STATUS.RESET,
        "",
        0,
          () => {
          this.question = "";
          this.cancelRequested = false;
          this.pendingLogId = 0;
          this.isLoading = false;
          this.$store.dispatch("setAiChatTraceId", {
            snsType: this.snsType,
            trace_id: DEFAULT_TRACE_ID,
          });
          // ブラウザ履歴を消して
          this.$store.dispatch("resetAiChatMessage", this.snsType);
          // start
          this.startChat()
        },
          () => {}
      );
    },
    handleSubmitButtonClick(){
      if (this.canCancelRequest) {
        this.cancelRequested = true;
        this.cancelChat(this.pendingLogId);
        return;
      }
      this.sendChat();
    },
    sendChat(){
      // 空欄判定 連打判定
      if( !this.canSendQuestion || this.isLoading ){
        return ;
      }
      const message = this.question;

      this.cancelRequested = false;
      this.setPendingState({
        status: "submitting",
        log_id: 0,
        question: message,
      });
      this.addUserMessage(message);
      this.isLoading = true;
      this.renderMessage();

      this.getSuggestSearchCondfig(
        CHAT_STATUS.QUESTION,
        message,
        0,
        res => {
          this.question = "";
          this.pendingLogId = res.data.logId;
          this.setLastMessageLogId(res.data.logId);
          this.setPendingState({
            status: "waiting",
            log_id: res.data.logId,
          });
          if(this.trace_id === DEFAULT_TRACE_ID) {
            this.$store.dispatch("setAiChatTraceId", {
              snsType: this.snsType,
              trace_id: res.data.traceId,
            });
          }
          if (this.cancelRequested) {
            this.cancelChat(res.data.logId);
            return;
          }
          this.scheduleNextPoll(res.data.logId);
        },
          () => {
          this.finalizePendingRequest();
        }
      );
    },
    cancelChat(logId){
      if (!logId) {
        return;
      }
      this.setPendingState({
        status: "canceling",
        log_id: logId,
      });
      this.getSuggestSearchCondfig(
        CHAT_STATUS.CANCEL,
        "",
        logId,
          () => {
          this.question = "";
          this.finalizePendingRequest();
          this.addMessage({
            side: "left",
            text: "送信を取り消しました。"
          });
        },
          () => {
          this.finalizePendingRequest();
        }
      );
    },
    scheduleNextPoll(logId) {
      this.setPendingState({
        status: "waiting",
        log_id: logId,
      });
      this.pollTimerId = setTimeout(() => {
        this.pollTimerId = null;
        this.pollChatResult(logId);
      }, POLL_DELAY_MS);
    },
    pollChatResult(logId){
      this.getSuggestSearchCondfig(
        CHAT_STATUS.CHECK_ANSWER,
        "",
        logId,
        res => {
          if(res.data.status !== "ok"){
            throw res;
          }
          const logStatus = res.data.logStatus;
          if (logStatus === LOG_STATUS.WAIT_RESPONSE) {
            this.scheduleNextPoll(logId);
            return;
          }
          if (logStatus === LOG_STATUS.ACTIVE) {
            this.addAiMessage(
              res.data.logId,
              res.data.hitCount,
              res.data.aiResponse,
              res.data.searchCriteriaPreviews,
              res.data.userMessages
            );
            this.addLinkMessage(res.data.logId, res.data.convertedSearchCriteria);
          } else if (logStatus === LOG_STATUS.CANCEL) {
            this.finalizePendingRequest();
            return;
          } else {
            this.addMessage({
              side: "left",
              text: this.getErrorMessage(res.data)
            });
          }
          this.finalizePendingRequest();
        },
          () => {
          this.finalizePendingRequest();
        }
      );
    },
    finalizePendingRequest(){
      if (this.pollTimerId) {
        clearTimeout(this.pollTimerId);
        this.pollTimerId = null;
      }
      this.pendingLogId = 0;
      this.cancelRequested = false;
      this.isLoading = false;
      this.setPendingState({
        status: "idle",
        log_id: 0,
        question: "",
      });
    },
    setPendingState(pending) {
      this.$store.dispatch("setAiChatPending", {
        snsType: this.snsType,
        pending: pending,
      });
    },
    setLastMessageLogId(logId) {
      this.$store.dispatch("setAiChatLastMessageLogId", {
        snsType: this.snsType,
        logId: logId,
      });
    },
    getLatestMessageLogId() {
      for (let index = this.messages.length - 1; index >= 0; index -= 1) {
        const message = this.messages[index];
        if (message && message.logId) {
          return message.logId;
        }
      }
      return 0;
    },
    shouldResumePendingRequest(activeLogId, logStatus) {
      if (!activeLogId) {
        return false;
      }
      const pending = this.pendingState || {};
      // uiが返信待ちならpending
      if (pending.status !== "idle" && pending.question) {
        return true;
      }
      // apiのstatusがレスポンス待ちじゃないならpendingではない
      if (logStatus !== LOG_STATUS.WAIT_RESPONSE) {
        return false;
      }
      // pending中のidがapiのidと一致すればpending
      if (pending.log_id && pending.log_id === activeLogId) {
        return true;
      }
      // uiの履歴末尾とapiのidが一致すればpendig
      return this.getLatestMessageLogId() === activeLogId;
    },
    hasRenderedPendingQuestion(question) {
      if (!question) {
        return false;
      }
      return this.messages.some(message => {
        return message.side === "right" && message.text === question;
      });
    },
    resumePendingRequest(activeLogId) {
      const pending = this.pendingState || {};
      if (!activeLogId) {
        this.finalizePendingRequest();
        return;
      }
      if (pending.status === "idle" && !pending.question) {
        this.finalizePendingRequest();
        return;
      }
      if (pending.question && !this.hasRenderedPendingQuestion(pending.question)) {
        this.addUserMessage(pending.question);
      }
      this.pendingLogId = activeLogId;
      this.isLoading = true;
      this.cancelRequested = pending.status === "canceling";
      this.setPendingState({
        log_id: activeLogId,
        status: pending.status === "canceling" ? "canceling" : "waiting",
      });
      this.pollChatResult(activeLogId);
    },
    addUserMessage(message){
      this.addMessage({side: "right", text: message})
    },
    addAiMessage(logId, hitCount, aiResponse, previews, userMessages){
      const message = ` <p>ありがとうございます。</br>` +
        `<ul>` +  
        userMessages.map(v => `<li>` + v + `</li>`).join("") +
        `</ul>` +  
        `を探しました。</p>` +

        `<p>検索条件を作成し、ヒット件数だけ確認しました。</br>` +
        `「目安件数：約` + 
        hitCount + 
        `件（※キーワード適用前）」</p>` + 

        `<p><span class="ai-chat-text-html-heading">検索条件(プレビュー)</span></br>` + 
        `<ul>` +  
        previews.map(v => `<li>` + v + `</li>`).join("") + `</p>` + 
        `</ul>` +  

        `<p><span class="ai-chat-text-html-heading">提案内容</span></br>` + 
        aiResponse.search_comment + `</p>` +

        `<p><span class="ai-chat-text-html-heading">検索条件に反映できなかった内容</span></br>` +
        aiResponse.unsupport_comment + `</p>` +

        `<p>この条件で検索する場合は、下のボタンを押してください (押下後、変更できます)</br>
        <strong>条件を微調整したい場合は、このままチャットに「予算を50万に変更して」「コスメを追加して」などと入力してください。</strong></p>`.trim();
      this.addMessage({
        side: "left",
        logId: logId,
        text: message,
        isHtml: true
      })
    },
    addLinkMessage(logId, condition){
      this.addMessage({
        side: "button",
        text: 'この条件で検索する',
        type: 'link',
        logId: logId,
        condition: condition
      })
    },
    addMessage(message){
      this.$store.dispatch("addAiChatMessage", {
        snsType: this.snsType,
        message: message
      });
      this.renderMessage();
    },
    renderMessage(){
      this.$nextTick(() => {
        const el = this.$refs.chatBody;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    searchBySuggest(message){
      // モーダルを消す
      this.closeModal();
      // 検索結果に移動する
      this.$emit("search", message.condition);
    },
    getErrorMessage(payload) {
      if( payload === null ){
        return "ネットワークエラーが発生しました";
      }
      return payload.error;
    },
    getSuggestSearchCondfig(status, message, logId, responseFunc, errorFunc){
      this.axios
        .get(SuggestSearchConfigAPI, {
          params: {
            sns_type: this.snsType,
            trace_id: this.trace_id,
            message: message,
            status: status,
            token: this.token,
            user_token: this.user_token,
            log_id: logId
          }
        })
        .then((response) => {
          if(response.data.status === "ok"){
            this.checkVersion(response.data.version);
            responseFunc(response);
          }else{
            const shouldSuppressError = errorFunc(response) === true;
            if (shouldSuppressError) {
              return;
            }
            this.addMessage({
              side: "left",
              text: this.getErrorMessage(response.data)
            });
            this.isLoading = false;

          }
        })
        .catch(e => {
          console.log(e )
          const shouldSuppressError = errorFunc(e) === true;
          if (shouldSuppressError) {
            return;
          }
          this.addMessage({
            side: "left",
            text: this.getErrorMessage(e.response ? e.response.data : null)
          });
          this.isLoading = false;
        });
    },
    checkVersion(version){
      if( this.version === DEFAULT_VERSION ){
        this.$store.dispatch("setAiChatVersion", version);
      }
    },
  },
  beforeDestroy() {
    if (this.pollTimerId) {
      clearTimeout(this.pollTimerId);
      this.pollTimerId = null;
    }
    if (this.appendedToBody && this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  }
};
</script>

<style scoped>
.ai-chat-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 16px 0 0;
}

.ai-chat-modal {
  width: 70%;
  min-height: 220px;
  max-height: calc(100vh - 16px);
  height: auto;
  margin-right: 10%;
  background: #ffffff;
  border-left: 1px solid #eee;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-chat-modal.dark {
  background: #151b26;
  border-left-color: #1f2a36;
}

.ai-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  font-weight: 700;
}

.ai-chat-modal.dark .ai-chat-header {
  border-bottom-color: #1f2a36;
}

.ai-chat-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-chat-title {
  font-size: 16px;
  color: #333;
}

.ai-chat-modal.dark .ai-chat-title {
  color: #f0f3f7;
}

.ai-chat-close {
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: #666;
}

.ai-chat-close:hover {
  color: #333;
}

.ai-chat-modal.dark .ai-chat-close {
  color: #9aa7b4;
}

.ai-chat-modal.dark .ai-chat-close:hover {
  color: #f0f3f7;
}

.ai-chat-body {
  flex: 0 1 auto;
  max-height: calc(100vh - 180px);
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: #333;
}

.ai-chat-modal.dark .ai-chat-body {
  color: #e1e6ee;
}

.ai-chat-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.ai-chat-row.right {
  flex-direction: row-reverse;
}

.ai-chat-row.link .ai-chat-avatar {
  visibility: hidden;
}

.ai-chat-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e9eef2;
  color: #4b5a6a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex: 0 0 auto;
}

.ai-chat-avatar i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 16px;
  line-height: 16px;
  text-align: center;
}

.ai-chat-modal.dark .ai-chat-avatar {
  background: #2b3a49;
  color: #d7e0ea;
}

.ai-chat-bubble {
  max-width: 70%;
  background: #f3f6f9;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.ai-chat-loading-bubble {
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

.ai-chat-loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #8a96a3;
  opacity: 0.4;
  animation: ai-chat-dot 1.2s infinite ease-in-out;
}

.ai-chat-loading-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.ai-chat-loading-dot:nth-child(3) {
  animation-delay: 0.4s;
}

.ai-chat-modal.dark .ai-chat-loading-dot {
  background: #93a2b3;
}

.ai-chat-row.right .ai-chat-bubble {
  background: #dfe9f7;
}

.ai-chat-modal.dark .ai-chat-bubble {
  background: #1f2a36;
}

.ai-chat-modal.dark .ai-chat-row.right .ai-chat-bubble {
  background: #223243;
}

@keyframes ai-chat-dot {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

.ai-chat-link-bubble {
  background: transparent;
  padding: 0;
}

.ai-chat-link-button {
  display: inline-block;
  width: 100%;
  background: #67c23a;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.ai-chat-link-button:hover {
  opacity: 0.9;
}

.ai-chat-text {
  font-size: 13px;
  line-height: 2.5;
  white-space: pre-line;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ai-chat-text-html {
  white-space: normal;
}

.ai-chat-text-html p {
  margin: 0;
}

.ai-chat-text-html /deep/ ul {
  margin: 0;
  padding-left: 5px;
}

.ai-chat-text-html /deep/ li {
  margin: 0;
}

.ai-chat-text-html /deep/ .ai-chat-text-html-num {
  color: #066;
}

.ai-chat-text-html /deep/ .ai-chat-text-html-string {
  color: #343434;
}

.ai-chat-text-html /deep/ .ai-chat-text-html-heading {
  font-size: 15px;
  font-weight: 700;
}

.ai-chat-time {
  font-size: 11px;
  color: #8a96a3;
  text-align: right;
}

.ai-chat-modal.dark .ai-chat-time {
  color: #93a2b3;
}

.ai-chat-input {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
}

.ai-chat-input-wrap {
  display: flex;
  flex-direction: column;
}

.ai-chat-modal.dark .ai-chat-input {
  border-top-color: #1f2a36;
}

.ai-chat-note {
  padding: 2px 16px 12px;
  font-size: 11px;
  line-height: 1.4;
  text-align: center;
  letter-spacing: 0.01em;
  color: #8f99a6;
}

.ai-chat-modal.dark .ai-chat-note {
  color: rgba(240, 243, 247, 0.78);
}

.ai-chat-input-field {
  flex: 1;
  min-height: 72px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  padding: 8px 10px;
  font-size: 13px;
  background: #fff;
  color: #333;
  line-height: 1.5;
  resize: vertical;
}

.ai-chat-modal.dark .ai-chat-input-field {
  background: #111923;
  border-color: #2b3a49;
  color: #e1e6ee;
}

.ai-chat-send {
  height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  border: none;
  background: #2f6bff;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.ai-chat-send.is-cancel {
  background: #d9534f;
}

.ai-chat-send.is-loading,
.ai-chat-send:disabled {
  background: #a7b8d8;
  cursor: not-allowed;
}

.ai-chat-send:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .ai-chat-overlay {
    padding: 8px 0 0;
  }

  .ai-chat-modal {
    width: calc(100% - 16px);
    margin-right: 8px;
    max-height: calc(100vh - 8px);
  }

  .ai-chat-body {
    max-height: calc(100vh - 168px);
  }
}
</style>
